/**
 * Deepwake module catalog — all original. Slot tiers: weapon / system / rig.
 * Combat resolves server-side at 1 Hz: hit quality is live transversal vs
 * tracking (the EVE depth), heat and capacitor are the active layer you play.
 */

export type SlotKind = 'weapon' | 'system' | 'rig';

export interface ModuleDef {
  key: string;
  name: string;
  slot: SlotKind;
  tier: 1 | 2;
  desc: string;
  price: number;
  masteryReq?: { track: string; level: number };
  // Active module fields
  cycleS?: number; // cycle time seconds
  capUse?: number; // capacitor per cycle
  heat?: number; // heat per cycle when overheated-adjacent (all actives build heat)
  // Weapons
  dps?: number; // damage per cycle
  optimal?: number; // meters
  falloff?: number;
  tracking?: number; // rad/s it can follow
  // Mining
  oreYield?: number; // units per cycle at resonance 1.0
  // Passive/system effects
  effects?: Partial<{
    maxVelMult: number;
    shieldHp: number;
    armorHp: number;
    shieldRegen: number;
    capacitor: number;
    capRegen: number;
    cargo: number;
    hullHp: number;
    repairHps: number; // active repairer
    warpFactorMult: number;
    trackingMult: number;
    oreYieldMult: number;
    resonanceBand: number; // widens mining sweet spot
    scramble: number; // warp scramble strength
  }>;
}

export const MODULES: Record<string, ModuleDef> = {
  // ---- Weapons (tier 1)
  ember_repeater: {
    key: 'ember_repeater', name: 'Ember Repeater', slot: 'weapon', tier: 1,
    desc: 'Rapid twin autocannon. Forgiving tracking, short reach.',
    price: 900, cycleS: 3, capUse: 4, heat: 7, dps: 42, optimal: 4_000, falloff: 6_000, tracking: 0.14,
  },
  lance_driver: {
    key: 'lance_driver', name: 'Lance Driver', slot: 'weapon', tier: 1,
    desc: 'Spinal rail. Hits like a gate segment at range; hopeless up close.',
    price: 1_600, cycleS: 5, capUse: 11, heat: 9, dps: 95, optimal: 22_000, falloff: 8_000, tracking: 0.03,
  },
  breakwater_rack: {
    key: 'breakwater_rack', name: 'Breakwater Rack', slot: 'weapon', tier: 1,
    desc: 'Dumb-loyal missiles. Ignore transversal, lose punch on fast hulls.',
    price: 1_400, cycleS: 6, capUse: 6, heat: 8, dps: 78, optimal: 26_000, falloff: 0, tracking: 99,
  },
  maw_cutter: {
    key: 'maw_cutter', name: 'Maw Cutter', slot: 'weapon', tier: 1,
    desc: 'Mining beam. Sing to the rock and the rock opens.',
    price: 700, cycleS: 5, capUse: 6, heat: 6, oreYield: 14, optimal: 12_000,
  },
  // ---- Weapons (tier 2)
  ember_cascade: {
    key: 'ember_cascade', name: 'Ember Cascade', slot: 'weapon', tier: 2,
    desc: 'Quad rotary cannon off a Vigil escort pattern.',
    price: 6_500, masteryReq: { track: 'gunnery', level: 2 },
    cycleS: 3, capUse: 6, heat: 9, dps: 68, optimal: 5_000, falloff: 7_500, tracking: 0.12,
  },
  gatesong_driver: {
    key: 'gatesong_driver', name: 'Gatesong Driver', slot: 'weapon', tier: 2,
    desc: 'Rail wound with gatesong alloy. The shot arrives slightly before the sound of it.',
    price: 9_800, masteryReq: { track: 'gunnery', level: 3 },
    cycleS: 5, capUse: 15, heat: 11, dps: 150, optimal: 30_000, falloff: 10_000, tracking: 0.028,
  },
  deep_maw: {
    key: 'deep_maw', name: 'Deep Maw Strip Beam', slot: 'weapon', tier: 2,
    desc: 'Industrial strip beam. Eats drifts whole.',
    price: 5_200, masteryReq: { track: 'mining', level: 2 },
    cycleS: 5, capUse: 10, heat: 8, oreYield: 30, optimal: 15_000,
  },

  // ---- Systems (tier 1)
  shield_ward: {
    key: 'shield_ward', name: 'Shield Ward', slot: 'system', tier: 1,
    desc: 'Boosts the shield envelope.',
    price: 800, effects: { shieldHp: 350, shieldRegen: 1.2 },
  },
  plate_lattice: {
    key: 'plate_lattice', name: 'Plate Lattice', slot: 'system', tier: 1,
    desc: 'Bolt-on armor plates, honest and heavy.',
    price: 800, effects: { armorHp: 450 },
  },
  afterburner: {
    key: 'afterburner', name: 'Afterburner', slot: 'system', tier: 1,
    desc: 'Overdriven drive tuning. Active: burns cap for speed.',
    price: 1_100, cycleS: 10, capUse: 8, heat: 5, effects: { maxVelMult: 1.9 },
  },
  cap_cell: {
    key: 'cap_cell', name: 'Capacitor Cell', slot: 'system', tier: 1,
    desc: 'More juice between the walls.',
    price: 700, effects: { capacitor: 260, capRegen: 0.9 },
  },
  cargo_pods: {
    key: 'cargo_pods', name: 'Cargo Pods', slot: 'system', tier: 1,
    desc: 'External pods. Aerodynamics is not a space problem.',
    price: 600, effects: { cargo: 320 },
  },
  repair_bots: {
    key: 'repair_bots', name: 'Repair Bots', slot: 'system', tier: 1,
    desc: 'Active hull/armor repair swarm.',
    price: 1_300, cycleS: 6, capUse: 9, heat: 6, effects: { repairHps: 26 },
  },
  resonator_array: {
    key: 'resonator_array', name: 'Resonator Array', slot: 'system', tier: 1,
    desc: 'Widens the resonance band your mining beams can hold.',
    price: 900, effects: { resonanceBand: 0.5, oreYieldMult: 1.1 },
  },
  warp_tuner: {
    key: 'warp_tuner', name: 'Warp Core Tuner', slot: 'system', tier: 1,
    desc: 'Shaves the align and hurries the tunnel.',
    price: 1_000, effects: { warpFactorMult: 1.35 },
  },
  // ---- Systems (tier 2)
  ward_matrix: {
    key: 'ward_matrix', name: 'Ward Matrix', slot: 'system', tier: 2,
    desc: 'Layered shield harmonics from Tidemark convoy doctrine.',
    price: 5_600, masteryReq: { track: 'engineering', level: 2 },
    effects: { shieldHp: 900, shieldRegen: 3.0 },
  },
  stillwake_scrambler: {
    key: 'stillwake_scrambler', name: 'Stillwake Scrambler', slot: 'system', tier: 2,
    desc: 'Active: holds a target out of warp. The Unbound signature tool.',
    price: 7_400, masteryReq: { track: 'navigation', level: 2 },
    cycleS: 4, capUse: 7, heat: 6, optimal: 9_000, effects: { scramble: 1 },
  },

  // ---- Rigs (passive, tier 1)
  gunnery_rig: {
    key: 'gunnery_rig', name: 'Fire-Control Rig', slot: 'rig', tier: 1,
    desc: '+15% tracking.', price: 2_000, effects: { trackingMult: 1.15 },
  },
  hull_rig: {
    key: 'hull_rig', name: 'Keelweld Rig', slot: 'rig', tier: 1,
    desc: '+300 hull.', price: 2_000, effects: { hullHp: 300 },
  },
  mining_rig: {
    key: 'mining_rig', name: 'Maw Focus Rig', slot: 'rig', tier: 1,
    desc: '+20% ore yield.', price: 2_000, effects: { oreYieldMult: 1.2 },
  },
  engine_rig: {
    key: 'engine_rig', name: 'Drive Bore Rig', slot: 'rig', tier: 1,
    desc: '+12% max velocity.', price: 2_000, effects: { maxVelMult: 1.12 },
  },
  cap_rig: {
    key: 'cap_rig', name: 'Deep Cell Rig', slot: 'rig', tier: 1,
    desc: '+25% cap regen.', price: 2_000, effects: { capRegen: 1.6 },
  },
  bay_rig: {
    key: 'bay_rig', name: 'Hollow Hull Rig', slot: 'rig', tier: 1,
    desc: '+25% cargo.', price: 2_000, effects: { cargo: 260 },
  },
};

/** Mastery tracks: progress purely by doing. Thresholds are activity counts. */
export const MASTERY_TRACKS = ['gunnery', 'mining', 'navigation', 'industry', 'trade', 'engineering'] as const;
export type MasteryTrack = (typeof MASTERY_TRACKS)[number];

/** Level N requires LEVELS[N] lifetime points on that track. Minutes, not weeks. */
export const MASTERY_LEVELS = [0, 20, 90, 300, 900, 2500];

export function masteryLevel(points: number): number {
  let lvl = 0;
  for (let i = 1; i < MASTERY_LEVELS.length; i++) if (points >= MASTERY_LEVELS[i]) lvl = i;
  return lvl;
}

/** Ship hull mastery gates (by-doing, minutes-scale): key -> requirements. */
export const HULL_REQS: Record<string, { track: MasteryTrack; level: number }[]> = {
  skiff: [],
  dart: [],
  mule: [{ track: 'industry', level: 1 }],
  lance: [{ track: 'gunnery', level: 2 }],
  reliquary: [{ track: 'gunnery', level: 3 }, { track: 'navigation', level: 2 }],
  bastion: [{ track: 'industry', level: 3 }, { track: 'engineering', level: 2 }],
};
